import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Heading,
    Spinner,
    Center,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Select,
    useToast,
    HStack,
    VStack,
    IconButton,
    Tooltip,
    Tag,
    Text,
    Flex,
    Icon,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
} from "@chakra-ui/react";
import { LuPencil, LuPlus } from "react-icons/lu";
import API from "../../../helpers/axiosInstance";

// Groups the flat page list under each parent_id so it can be rendered as a
// nested FAQ-style accordion. A page whose parent_id doesn't match anything
// in the list (deleted parent, bad data, etc.) is treated as top-level
// rather than silently dropped.
const buildTree = (items, parentId = null) => {
    const ids = new Set(items.map((item) => item.id));
    return items
        .filter((item) => {
            const effectiveParent = ids.has(item.parent_id) ? item.parent_id : null;
            return effectiveParent === parentId;
        })
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((item) => ({ ...item, children: buildTree(items, item.id) }));
};

// A single page, shown inline (name, slug, sort, edit) — used both for leaf
// pages and for the always-visible row inside an expanded parent.
const PageRow = ({ node, depth, onEdit }) => (
    <Flex
        align="center"
        justify="space-between"
        gap={3}
        py={2.5}
        pl={4 + depth * 5}
        pr={3}
        _hover={{ bg: "gray.50" }}
    >
        <HStack spacing={3} minW={0}>
            <Text
                fontWeight={depth === 0 ? "600" : "400"}
                fontSize="md"
                color="gray.800"
                noOfLines={1}
            >
                {node.name}
            </Text>
            <Text fontFamily="mono" fontSize="sm" color="gray.500" noOfLines={1}>
                {node.slug}
            </Text>
        </HStack>
        <HStack spacing={2} flexShrink={0}>
            <Tag size="sm" variant="subtle" colorScheme="gray">
                #{node.sort_order}
            </Tag>
            <Tooltip label="Edit page" fontSize="xs">
                <IconButton
                    aria-label="Edit page"
                    icon={<Icon as={LuPencil} boxSize={3.5} />}
                    size="xs"
                    variant="ghost"
                    color="gray.500"
                    _hover={{ bg: "#eff6ff", color: "#2563eb" }}
                    onClick={() => onEdit(node)}
                />
            </Tooltip>
        </HStack>
    </Flex>
);

// Recursive accordion node: a leaf page is just a row; a page with children
// becomes an expandable "question" whose "answer" is its own child pages,
// which can themselves expand further (e.g. Settings -> User Management ->
// Roles and Permission).
const PageNode = ({ node, depth, onEdit }) => {
    if (node.children.length === 0) {
        return (
            <AccordionItem border="none" borderBottom="1px solid" borderColor="gray.100">
                <PageRow node={node} depth={depth} onEdit={onEdit} />
            </AccordionItem>
        );
    }

    return (
        <AccordionItem border="none" borderBottom="1px solid" borderColor="gray.100">
            <HStack spacing={0} _hover={{ bg: "gray.50" }}>
                <AccordionButton py={3} pl={4 + depth * 5} pr={2} _hover={{ bg: "transparent" }}>
                    <HStack flex="1" spacing={3} minW={0} textAlign="left">
                        <Text
                            fontWeight={depth === 0 ? "600" : "500"}
                            fontSize={depth === 0 ? "md" : "sm"}
                            color="gray.800"
                            noOfLines={1}
                        >
                            {node.name}
                        </Text>
                        <Text fontFamily="mono" fontSize="sm" color="gray.500" noOfLines={1}>
                            {node.slug}
                        </Text>
                        <Tag size="sm" variant="subtle" colorScheme="blue" flexShrink={0} fontWeight="500">
                            {node.children.length} sub-page{node.children.length > 1 ? "s" : ""}
                        </Tag>
                    </HStack>
                    <AccordionIcon color="gray.400" />
                </AccordionButton>
                <Tooltip label="Edit page" fontSize="xs">
                    <IconButton
                        aria-label="Edit page"
                        icon={<Icon as={LuPencil} boxSize={3.5} />}
                        size="xs"
                        variant="ghost"
                        color="gray.500"
                        mx={2}
                        flexShrink={0}
                        _hover={{ bg: "#eff6ff", color: "#2563eb" }}
                        onClick={() => onEdit(node)}
                    />
                </Tooltip>
            </HStack>
            <AccordionPanel p={0} bg="gray.50">
                <Accordion allowMultiple>
                    {node.children.map((child) => (
                        <PageNode key={child.id} node={child} depth={depth + 1} onEdit={onEdit} />
                    ))}
                </Accordion>
            </AccordionPanel>
        </AccordionItem>
    );
};

const Pages = () => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        id: "",
        name: "",
        slug: "",
        parent_id: "",
        sort_order: "",
    });
    const [isEdit, setIsEdit] = useState(false);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const getApiHeaders = () => ({
        "x-access-token": localStorage.getItem("user"),
    });

    const fetchPages = async () => {
        try {
            setLoading(true);
            const res = await API.get(`${process.env.REACT_APP_API_URL}/pages/list`, {
                headers: getApiHeaders(),
            });
            setPages(res.data.data || []);
        } catch (err) {
            toast({
                title: "Failed to load pages",
                status: "error",
                duration: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPages();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                name: formData.name,
                slug: formData.slug,
                parent_id: formData.parent_id ? Number(formData.parent_id) : null,
                sort_order: Number(formData.sort_order),
            };

            if (isEdit) {
                await API.post(`${process.env.REACT_APP_API_URL}/pages/update`, {
                    id: formData.id,
                    ...payload,
                }, { headers: getApiHeaders() });
                toast({ title: "Page updated successfully", status: "success" });
            } else {
                await API.post(`${process.env.REACT_APP_API_URL}/pages/create`, payload, { headers: getApiHeaders() });
                toast({ title: "Page created successfully", status: "success" });
            }
            onClose();
            fetchPages();
        } catch (err) {
            toast({
                title: "Failed to save page",
                description: err.message,
                status: "error",
            });
        }
    };

    const handleEdit = (page) => {
        setIsEdit(true);
        setFormData({
            id: page.id,
            name: page.name,
            slug: page.slug,
            parent_id: page.parent_id || "",
            sort_order: page.sort_order,
        });
        onOpen();
    };

    const handleAddNew = () => {
        setIsEdit(false);
        setFormData({
            id: "",
            name: "",
            slug: "",
            parent_id: "",
            sort_order: "",
        });
        onOpen();
    };

    const tree = buildTree(pages);

    return (
        <Box p={6} ml={4} className="box-style-card" w={"100%"}>
            <HStack justify="space-between" mb={6} align="flex-start">
                <Box>
                    <Heading size="lg" fontWeight="600" color="gray.900">Pages Management</Heading>
                    <Text fontSize="sm" color="gray.500" mt="4px">
                        Define the pages available across the portal and how they nest under each other.
                    </Text>
                </Box>
                <Button
                    size="sm"
                    leftIcon={<Icon as={LuPlus} />}
                    onClick={handleAddNew}
                    bg="#2563eb"
                    color="white"
                    _hover={{ bg: "#1d4ed8" }}
                >
                    Add New Page
                </Button>
            </HStack>

            {loading ? (
                <Center py={12}>
                    <Spinner size="lg" color="blue.500" thickness="3px" />
                </Center>
            ) : tree.length === 0 ? (
                <Center py={12}>
                    <Text color="gray.500" fontSize="sm">No pages found.</Text>
                </Center>
            ) : (
                <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" bg="white">
                    <Accordion allowMultiple>
                        {tree.map((node) => (
                            <PageNode key={node.id} node={node} depth={0} onEdit={handleEdit} />
                        ))}
                    </Accordion>
                </Box>
            )}

            {/* Modal for Create / Edit */}
            <Modal isOpen={isOpen} onClose={onClose} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">{isEdit ? "Edit Page" : "Add New Page"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Name</FormLabel>
                                <Input size="sm" name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Reports" />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Slug</FormLabel>
                                <Input
                                    size="sm"
                                    fontFamily="mono"
                                    fontSize="sm"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleChange}
                                    placeholder="e.g. reports/usage-report"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Parent Page</FormLabel>
                                <Select
                                    size="sm"
                                    placeholder="None (top-level)"
                                    name="parent_id"
                                    value={formData.parent_id}
                                    onChange={handleChange}
                                >
                                    {pages
                                        .filter((p) => !isEdit || p.id !== formData.id)
                                        .map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Sort Order</FormLabel>
                                <Input
                                    size="sm"
                                    name="sort_order"
                                    type="number"
                                    value={formData.sort_order}
                                    onChange={handleChange}
                                    placeholder="0"
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" size="sm" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSubmit}
                            bg="#2563eb"
                            color="white"
                            _hover={{ bg: "#1d4ed8" }}
                        >
                            {isEdit ? "Update" : "Create"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Pages;
